var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Utils } from '../../utils';
import { CustomTypedEvent } from '../../events';
import { PacemEventTarget } from '../../eventtarget';
import { PCSS } from '../../prefix';
import { Watch } from '../../decorators';
import { PropertyConverters } from '../../converters';
export const RepeaterItemCreateEventName = 'repeateritemcreate';
export const RepeaterItemRemoveEventName = 'repeateritemremove';
export class RepeaterItemEvent extends CustomTypedEvent {
    constructor(name, args) {
        super(name, args);
    }
}
export class RepeaterItemCreateEvent extends RepeaterItemEvent {
    constructor(args) {
        super(RepeaterItemCreateEventName, args);
    }
}
export class RepeaterItemRemoveEvent extends RepeaterItemEvent {
    constructor(args) {
        super(RepeaterItemRemoveEventName, args);
    }
}
const STD_EVENTS = ['keydown', 'keyup', 'click', 'focus', 'blur', 'dblclick', 'mouseover', 'mouseout', 'mouseenter', 'mouseleave', 'mousedown', 'mouseup', 'mousemove', 'contextmenu', 'wheel'];
class ElementAriaAttributes {
    constructor(_element, ariaAttrs) {
        this._element = _element;
        this._aria = Utils.extend({}, ariaAttrs || {});
    }
    toObject() {
        return Utils.extend({}, this._aria);
    }
    set(name, value) {
        this._aria[name] = value;
        const el = this._element;
        if (el.isConnected) {
            el.setAttribute('aria-' + name, value);
        }
    }
    remove(name) {
        delete this._aria[name];
        const el = this._element;
        if (el.isConnected) {
            el.removeAttribute('aria-' + name);
        }
    }
}
class ElementAria {
    constructor(_element, role, attrs) {
        this._element = _element;
        this._aria = { role: role, attrs: new ElementAriaAttributes(_element, attrs) };
    }
    get role() {
        return this._aria.role;
    }
    /** Remarks: when set to an empty string AFTER the element gets connected to the DOM, this will remove the `role` attribute. */
    set role(role) {
        this._aria.role = role;
        const el = this._element;
        if (el.isConnected) {
            Utils.isNullOrEmpty(role) ? el.removeAttribute('role') : el.setAttribute('role', role);
        }
    }
    get attributes() {
        return this._aria.attrs;
    }
}
export class PacemElement extends PacemEventTarget {
    constructor(role, aria) {
        super();
        this.#cssBag = [];
        this.#parts = [];
        this._tabIndex = -1;
        this.behaviors = [];
        this._aria = new ElementAria(this, role, aria);
    }
    // #region ARIA
    get aria() { return this._aria; }
    // #endregion
    static #stdEvtsMemoizer = null;
    static get emittedEventTypes() {
        return this.#stdEvtsMemoizer ??= Utils.readonlyCopy(STD_EVENTS);
    }
    #cssBag;
    #parts;
    connectedCallback() {
        super.connectedCallback();
        if (!Utils.isNullOrEmpty(this._aria.role)) {
            this.setAttribute('role', this._aria.role);
        }
        let ariaAttrs = this._aria.attributes.toObject();
        if (!Utils.isNullOrEmpty(ariaAttrs)) {
            for (let name in ariaAttrs) {
                this.setAttribute('aria-' + name, ariaAttrs[name]);
            }
        }
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        STD_EVENTS.forEach(e => {
            this.addEventListener(e, this.emitHandler, false);
        });
        for (var behavior of this.behaviors || []) {
            behavior.register(this);
        }
    }
    disconnectedCallback() {
        for (var behavior of this.behaviors || []) {
            behavior.unregister(this);
        }
        STD_EVENTS.forEach(e => {
            this.removeEventListener(e, this.emitHandler, false);
        });
        super.disconnectedCallback();
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'css':
                for (var css in this.css) {
                    this.style.setProperty(css, this.css[css]);
                }
                break;
            case 'cssClass':
                if (Utils.isArray(this.cssClass)) {
                    const newBag = this.cssClass;
                    for (let css of this.#cssBag.splice(0)) {
                        Utils.removeClass(this, css);
                    }
                    for (let css of newBag) {
                        this.#cssBag.push(css);
                        Utils.addClass(this, css);
                    }
                }
                else
                    for (let css in this.cssClass) {
                        Utils.toggleClass(this, css, this.cssClass[css]);
                    }
                break;
            case 'cssPart':
                const cssPart = this.cssPart, bag = this.#parts;
                if (Utils.isArray(cssPart)) {
                    const newBag = cssPart;
                    for (let partName of bag.splice(0)) {
                        Utils.removePart(this, partName);
                    }
                    for (let partName of newBag) {
                        bag.push(partName);
                        Utils.addPart(this, partName);
                    }
                }
                else
                    for (let partName in cssPart) {
                        Utils.togglePart(this, partName, cssPart[partName]);
                    }
                break;
            case "hide":
                const hidingCss = (PCSS + '-hiding');
                if (val) {
                    const el = this, animCss = PCSS + '-anim', callback = () => {
                        el.setAttribute('hidden', '');
                        el.aria.attributes.set('hidden', 'true');
                    };
                    if (Utils.hasClass(el, animCss) && !Utils.isNull(old)) {
                        Utils.removeClass(el, animCss);
                        requestAnimationFrame(() => {
                            Utils.addClass(el, hidingCss);
                            Utils.addClass(el, animCss);
                            Utils.waitForAnimationEnd(el, 250).then(_ => {
                                if (el.hide) {
                                    callback();
                                }
                                Utils.removeClass(el, hidingCss);
                            });
                        });
                    }
                    else
                        callback();
                }
                else {
                    Utils.removeClass(this, hidingCss);
                    this.removeAttribute('hidden');
                    this.aria.attributes.remove('hidden');
                }
                break;
            case 'tooltip':
                this.title = val;
                Utils.isNullOrEmpty(val) ? this.aria.attributes.remove('label') : this.aria.attributes.set('label', val);
                break;
            case 'tabOrder':
                this._tabIndex = val;
                if (!this.disabled) {
                    this.tabIndex = val;
                }
                break;
            case 'behaviors':
                if (!Utils.isNullOrEmpty(old)) {
                    for (let behavior of old) {
                        behavior.unregister(this);
                    }
                }
                if (!Utils.isNullOrEmpty(val) && !first) {
                    for (let behavior of val) {
                        behavior.register(this);
                    }
                }
                break;
            case 'disabled':
                let cssDis = PCSS + '-' + name;
                if (!!val) {
                    this._tabIndex = this.tabIndex;
                    this.tabIndex = -1;
                    Utils.addClass(this, cssDis);
                }
                else {
                    this.tabIndex = this._tabIndex;
                    Utils.removeClass(this, cssDis);
                }
                break;
            case 'culture':
                if (Utils.isNullOrEmpty(val)) {
                    this.removeAttribute('lang');
                }
                else {
                    this.setAttribute('lang', val);
                }
                if (!first) {
                    this.refreshBindings();
                }
                break;
        }
    }
}
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json /* when declared plainly assume array of strings */ })
], PacemElement.prototype, "cssClass", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemElement.prototype, "cssPart", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemElement.prototype, "css", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], PacemElement.prototype, "hide", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemElement.prototype, "tooltip", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemElement.prototype, "tabOrder", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemElement.prototype, "culture", void 0);
__decorate([
    Watch({ emit: false })
], PacemElement.prototype, "behaviors", void 0);
export class PacemContentElement extends PacemElement {
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (name === 'content' && !first) {
            this._fillContent(val);
        }
    }
    _isContentNullOrEmpty(content = this.content) {
        return Utils.isNullOrEmpty(content) || (content instanceof DocumentFragment && content.childElementCount === 0);
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._original = Array.from(this.children);
        if (!this._isContentNullOrEmpty()) {
            this._fillContent();
        }
    }
    disconnectedCallback() {
        if (!Utils.isNullOrEmpty(this._original)) {
            this.innerHTML = '';
            Element.prototype.append.apply(this, this._original);
        }
        super.disconnectedCallback();
    }
    _fillContent(val = this.content) {
        const output = this.sanitize(val);
        if (Utils.isNullOrEmpty(output)) {
            this.innerHTML = '';
        }
        else {
            if (output instanceof Node) {
                this.innerHTML = '';
                this.append(output);
            }
            else {
                this.innerHTML = output;
            }
        }
    }
}
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemContentElement.prototype, "content", void 0);
export class PacemUnsafeContentElement extends PacemContentElement {
    sanitize(html) {
        return html;
    }
}
export class PacemSafeContentElement extends PacemContentElement {
    _sanitizeLegacy(html) {
        if (Utils.isNullOrEmpty(html)) {
            return html;
        }
        if (html instanceof Node) {
            for (let i = html.childNodes.length - 1; i >= 0; i--) {
                const node = html.childNodes.item(i);
                if (node instanceof HTMLScriptElement) {
                    node.remove();
                }
                else {
                    this.sanitize(node);
                }
            }
            return html;
        }
        else {
            const scriptBlockPattern = /<script.+<\/script>/g;
            // TODO: remove onclick, onload, onkey*, ... attributes
            return (html || '').toString().replace(scriptBlockPattern, '');
        }
    }
    sanitize(html) {
        // TODO: implement by using (when available in dom library)
        // https://developer.mozilla.org/en-US/docs/Web/API/Sanitizer/Sanitizer
        return this._sanitizeLegacy(html);
    }
}
//}
